// IMPORTS

import { init as initLogger, Logger } from './utils/logger';
import { exportScene } from './exporter';
import { createModelViewer } from './factories/model-viewer.factory';
import {
    checkWebGL,
    releaseResources,
    renderError,
    renderScene,
    renderWebGLError,
} from './renderers';
import * as THREE from 'three';
import { getObjectByName, getObjectsByType, getQueryParam } from './utils';
import {
    QUERY_PARAM__DISABLE_LIGHTS,
    NAME__ILLUMINANT,
    NAME__TOP_LIGHT,
    NAME_BOTTOM_LIGHT,
} from './constants';
import { createBackground } from './renderers/factories/background.factory';
import { createEnvironmentMap } from './renderers/factories/environment-map.factory';
import { disposePmremGenerator } from './renderers/factories/pmrem-generator.factory';

// INITIALIZATION AND RUN

_init({ logging: true });

export {
    create,
    exportScene,
    resetCamera,
    setEnvironmentLighting,
    setStudioLighting,
    startAutoRotate,
    toggleBottomLight,
    toggleEnvironmentMap,
    toggleIlluminant,
    toggleLights,
    toggleTopLight,
};

// IMPLEMENTATION DETAILS

function _init({ logging } = {}) {
    initLogger(logging);
    window.apiModelViewer = {
        create,
        exportScene,
        resetCamera,
        setBackgroundAndEnvironment,
        setEnvironmentLighting,
        setStudioLighting,
        startAutoRotate,
        toggleBottomLight,
        toggleEnvironmentMap,
        toggleIlluminant,
        toggleLights,
        toggleTopLight,
    };
}

async function create({
    selectorContainer,
    selectorBackgroundChooser,
    selectorToggleIlluminant,
    selectorRotateCamera,
    selectorResetCamera,
    selectorSetStudioLighting,
    selectorSetEnvironmentLighting,
    autoRotate,
    basePathBackground,
    basePathEnvironment,
    backgroundFileName,
    environmentFileName,
}) {
    if (!checkWebGL()) {
        renderWebGLError(selectorContainer);
        return;
    }

    try {
        // Select background first, as the following steps may take a while
        _selectBackground(
            selectorBackgroundChooser,
            backgroundFileName,
            environmentFileName
        );

        const modelViewer = await createModelViewer(selectorContainer, {
            autoRotate,
            basePathBackground,
            basePathEnvironment,
            backgroundFileName,
            environmentFileName,
        });
        renderScene(modelViewer);

        const ctrl = _createController({
            selectorContainer,
            selectorBackgroundChooser,
            selectorToggleIlluminant,
            selectorRotateCamera,
            selectorResetCamera,
            selectorSetStudioLighting,
            selectorSetEnvironmentLighting,
            modelViewer,
        });

        toggleLights(
            ctrl,
            getQueryParam(QUERY_PARAM__DISABLE_LIGHTS) !== 'true'
        );
        toggleIlluminant(ctrl, false);
        setStudioLighting(ctrl);

        _bindHandlers(ctrl);

        return ctrl;
    } catch (e) {
        renderError(selectorContainer);
        Logger.error(e);
        throw e;
    }
}

function resetCamera(ctrl) {
    const modelViewer = ctrl.modelViewer;
    modelViewer.controls.reset();
    modelViewer.camera.fov = modelViewer.initialValues.fov;
    modelViewer.camera.updateProjectionMatrix();
}

function toggleIlluminant(ctrl, visible) {
    _toggleLight(NAME__ILLUMINANT, ctrl.modelViewer.scene, visible);
}

function toggleBottomLight(ctrl, visible) {
    _toggleLight(NAME_BOTTOM_LIGHT, ctrl.modelViewer.scene, visible);
}

function toggleTopLight(ctrl, visible) {
    _toggleLight(NAME__TOP_LIGHT, ctrl.modelViewer.scene, visible);
}

function startAutoRotate(ctrl) {
    ctrl.modelViewer.controls.autoRotate = true;
}

function setStudioLighting(ctrl) {
    toggleBottomLight(ctrl, true);
    toggleTopLight(ctrl, true);
    toggleEnvironmentMap(ctrl, false);
    ctrl.status.environmentLighting = false;
}

function setEnvironmentLighting(ctrl) {
    toggleBottomLight(ctrl, false);
    toggleTopLight(ctrl, false);
    toggleEnvironmentMap(ctrl, true);
    ctrl.status.environmentLighting = true;
}

function toggleEnvironmentMap(ctrl, visible) {
    const modelViewer = ctrl.modelViewer;
    if ((visible == null && !modelViewer.scene.environment) || visible) {
        modelViewer.scene.environment = modelViewer.environmentMap;
        return;
    }
    modelViewer.scene.environment = null;
}

function toggleLights(ctrl, visible) {
    getObjectsByType(ctrl.modelViewer.scene, THREE.Light).forEach(
        (light) =>
            (light.visible = visible != null ? !!visible : !light.visible)
    );
}

async function setBackgroundAndEnvironment(ctrl, value) {
    const modelViewer = ctrl.modelViewer;
    const {
        renderer,
        props: {
            useEnvMapAsBackground,
            basePathBackground,
            basePathEnvironment,
        },
    } = modelViewer;
    const [backgroundFileName, environmentFileName] = value.split('␞');
    const [background, environment] = await Promise.all([
        createBackground(
            basePathBackground,
            backgroundFileName,
            useEnvMapAsBackground,
            {
                basePathEnvironment,
                environmentFileName,
            }
        ),
        createEnvironmentMap(renderer, environmentFileName, {
            basePath: basePathEnvironment,
        }),
    ]);
    // TODO don't set environment, if studio-lighting has been set
    modelViewer.scene.environment = environment;
    // Keep the backup, for the environment-toggle
    modelViewer.environmentMap = environment;
    modelViewer.scene.background = background;
    ctrl.status.environmentLighting
        ? setEnvironmentLighting(ctrl)
        : setStudioLighting(ctrl);
    releaseResources(environment);
}

// Private

function _createBackgroundChooserKey(background, environment) {
    return `${background}␞${environment}`;
}

function _selectBackground(selectorBackgroundChooser, background, environment) {
    if (!selectorBackgroundChooser) return;
    document.querySelector(
        selectorBackgroundChooser
    ).value = _createBackgroundChooserKey(background, environment);
}

function _createController({
    selectorContainer,
    selectorBackgroundChooser,
    selectorToggleIlluminant,
    selectorRotateCamera,
    selectorResetCamera,
    selectorSetStudioLighting,
    selectorSetEnvironmentLighting,
    modelViewer,
}) {
    return {
        selectors: {
            renderContainer: selectorContainer,
            backgroundChooser: selectorBackgroundChooser,
            toggleIlluminant: selectorToggleIlluminant,
            rotateCamera: selectorRotateCamera,
            resetCamera: selectorResetCamera,
            setStudioLighting: selectorSetStudioLighting,
            setEnvironmentLighting: selectorSetEnvironmentLighting,
        },
        status: {
            environmentLighting: false,
        },
        modelViewer,
    };
}

function _toggleLight(name, scene, visible) {
    const illuminant = getObjectByName(scene, name);
    if (!illuminant) return;
    getObjectsByType(illuminant, THREE.Light).forEach(
        (obj) => (obj.visible = visible != null ? !!visible : !obj.visible)
    );
}

function _bindHandlers(ctrl) {
    const {
        backgroundChooser: selectorBackgroundChooser,
        toggleIlluminant: selectorToggleIlluminant,
        rotateCamera: selectorRotateCamera,
        resetCamera: selectorResetCamera,
        setStudioLighting: selectorSetStudioLighting,
        setEnvironmentLighting: selectorSetEnvironmentLighting,
    } = ctrl.selectors;
    document
        .querySelector(selectorBackgroundChooser)
        ?.addEventListener('change', (evt) =>
            setBackgroundAndEnvironment(ctrl, evt.target.value)
        );
    document
        .querySelector(selectorToggleIlluminant)
        ?.addEventListener('click', () => toggleIlluminant(ctrl));
    document
        .querySelector(selectorRotateCamera)
        ?.addEventListener('click', () => startAutoRotate(ctrl));
    document
        .querySelector(selectorResetCamera)
        ?.addEventListener('click', () => resetCamera(ctrl));
    document
        .querySelector(selectorSetStudioLighting)
        ?.addEventListener('click', () => setStudioLighting(ctrl));
    document
        .querySelector(selectorSetEnvironmentLighting)
        ?.addEventListener('click', () => setEnvironmentLighting(ctrl));
    // Release final resources, before page unloads
    window.addEventListener('unload', () => disposePmremGenerator());
}
